/**
 * Main App Component (React Router Version)
 *
 * Fully migrated React application using React Router for navigation.
 * All pages are now React components - no more vanilla JS pages.
 * Uses lazy loading for route-based code splitting.
 */
import { Suspense, lazy, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import Header from './components/Header';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import PokeballLoader from './PokeballLoader';
import PageTransition from './components/PageTransition';
import ErrorBoundary from './ErrorBoundary';
import QueryProvider from './providers/QueryProvider';

// Lazy load pages for route-based code splitting
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const GlobalInventoryPage = lazy(() => import('./pages/GlobalInventoryPage'));
const ShowPreparationPage = lazy(() => import('./pages/ShowPreparationPage'));
const ScanPage = lazy(() => import('./pages/ScanPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));

function AppContent() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  return (
      <ToastProvider>
        {!isLoginPage && <Header />}
        {!isLoginPage && <KeyboardShortcuts />}
        <main
          id="main-content"
          className="min-h-screen bg-[var(--bg)] py-8"

        >
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen">
              <PokeballLoader />
            </div>
          }>
            <Routes>
              <Route path="/" element={
                <ProtectedRoute>
                  <PageTransition>
                    <DashboardPage />
                  </PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/login" element={
                <PageTransition>
                  <LoginPage />
                </PageTransition>
              } />
              {/* Scan */}
              <Route path="/scan" element={
                <ProtectedRoute>
                  <PageTransition>
                    <ScanPage />
                  </PageTransition>
                </ProtectedRoute>
              } />
              {/* Canonical redirects for legacy URLs */}
              <Route path="/watchlist" element={<Navigate to="/" replace />} />
              <Route path="/favorites" element={<Navigate to="/" replace />} />
              <Route path="/pricing" element={<Navigate to="/" replace />} />
              <Route path="/suggestions" element={<Navigate to="/" replace />} />
              {/* Campaigns */}
              <Route path="/campaigns" element={
                <ProtectedRoute>
                  <PageTransition>
                    <CampaignsPage />
                  </PageTransition>
                </ProtectedRoute>
              } />
              {/* Global Inventory */}
              <Route path="/inventory" element={
                <ProtectedRoute>
                  <PageTransition>
                    <GlobalInventoryPage />
                  </PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/shows" element={
                <ProtectedRoute><PageTransition><ShowPreparationPage /></PageTransition></ProtectedRoute>
              } />
              {/* Invoices */}
              <Route path="/invoices" element={
                <ProtectedRoute>
                  <PageTransition>
                    <InvoicesPage />
                  </PageTransition>
                </ProtectedRoute>
              } />
              {/* Admin page (accessible via header indicator for admins) */}
              <Route path="/admin" element={
                <AdminRoute>
                  <PageTransition>
                    <AdminPage />
                  </PageTransition>
                </AdminRoute>
               } />
               <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </main>
      </ToastProvider>
  );
}

function AppWithErrorBoundary() {
  const location = useLocation();
  const [sessionKey, setSessionKey] = useState<number | null>(null);
  return (
    <QueryProvider sessionKey={sessionKey} routeKey={location.pathname}>
      <ErrorBoundary key={location.pathname}>
        <AuthProvider onIdentityChange={setSessionKey}>
          <AppContent />
        </AuthProvider>
      </ErrorBoundary>
    </QueryProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppWithErrorBoundary />
    </BrowserRouter>
  );
}
